/**
 * DisclosureCard — shared disclosure display component.
 * Used by both UnitDisclosuresPage (standalone) and UnitDetailPage (inline tab).
 *
 * Renders disclosure status, metadata (dates, staleness, jurisdiction), and
 * delegates the action area to the caller via `children` (for button/link/form).
 */

import type { ReactNode } from 'react';
import type { UnitDisclosureListItem } from '../lib/api';
import { Badge } from './ui/badge';
import {
  getDisclosureStatus,
  formatDisclosureId,
  formatDisclosureDate,
  disclosureStatusStyles,
} from './disclosure-utils';

interface DisclosureCardProps {
  item: UnitDisclosureListItem;
  /** Extra classes on the outer container */
  className?: string;
  /** Action area (button, link, inline form, etc.) */
  children?: ReactNode;
}

export function DisclosureCard({ item, className, children }: DisclosureCardProps) {
  const status = getDisclosureStatus(item);
  const lastReviewed = item.record?.lastReviewedAt ?? null;
  const lastUpdated = item.record?.updatedAt ?? null;

  return (
    <div
      className={className ?? 'rounded-lg border border-border'}
      data-testid={`disclosure-card-${item.disclosureId}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
        <div className="flex-1 min-w-0">
          {/* Title + badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">
              {formatDisclosureId(item.disclosureId)}
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${disclosureStatusStyles[status]}`}
            >
              {status}
            </span>
            {item.isStale && (
              <Badge
                variant="outline"
                className="bg-warning/15 text-warning border-warning/25"
                aria-label="Disclosure data may be stale"
              >
                Stale
              </Badge>
            )}
            {item.required && (
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                Required
              </Badge>
            )}
          </div>

          {/* Jurisdiction */}
          {item.jurisdictionPath && (
            <p className="text-xs text-muted-foreground mt-1">
              Jurisdiction: <span className="font-medium">{item.jurisdictionPath}</span>
            </p>
          )}

          {/* Dates & review cadence */}
          <div className="flex gap-4 text-xs text-muted-foreground flex-wrap mt-1">
            {lastReviewed && <span>Last reviewed: {formatDisclosureDate(lastReviewed)}</span>}
            {lastUpdated && <span>Last updated: {formatDisclosureDate(lastUpdated)}</span>}
            {item.stalenessThresholdMonths && (
              <span>Review every {item.stalenessThresholdMonths} months</span>
            )}
          </div>

          {/* Contextual messages */}
          {item.isStale && (
            <p className="text-xs text-warning mt-1">
              This disclosure data may be outdated. Review and update before generating a lease.
            </p>
          )}
          {status === 'Included' && (
            <p className="text-xs text-muted-foreground mt-1">
              Informational document — automatically included with the lease package.
            </p>
          )}
        </div>

        {/* Action slot (inline — same row as header on desktop) */}
        {children}
      </div>
    </div>
  );
}
